import { RemoteWorkspace } from "@opencode/schema/remote-workspace"
import { SairAccount } from "@opencode/schema/sair-account"
import { Option, Schema } from "effect"

const Seconds = Schema.Int.check(Schema.isGreaterThan(0))
const Auth = Schema.Union([
  Schema.Struct({ authenticated: Schema.Literal(false) }),
  Schema.Struct({ authenticated: Schema.Literal(true), profile: SairAccount.Profile }),
])
const SignIn = Schema.Struct({
  verification_url: Schema.String,
  user_code: Schema.String,
  interval: Seconds,
  expires_at: Seconds,
})
const Poll = Schema.Union([
  Schema.Struct({ state: Schema.Literal("pending"), interval: Seconds }),
  Schema.Struct({ authenticated: Schema.Literal(true), profile: SairAccount.Profile, client_id: Schema.String }),
])
const Workspace = Schema.Struct({
  id: Schema.String,
  state: Schema.String,
  error: Schema.optional(Schema.String),
  resources: Schema.optional(RemoteWorkspace.Resources),
  pending_resources: Schema.optional(RemoteWorkspace.Resources),
  resource_error: Schema.optional(Schema.String),
  last_activity: Schema.optional(Schema.Int),
})
const Lease = Schema.Struct({
  workspace: Workspace,
  connection: Schema.Struct({ url: Schema.String }),
  expires_at: Seconds,
  auth_mode: Schema.Literal("sair"),
  protocol: Schema.Literal(1),
})
const Renewal = Schema.Struct({ state: Schema.String, expires_at: Seconds })
const Released = Schema.Struct({ released: Schema.Literal(true) })
const BrowserLogout = Schema.Struct({ signedOut: Schema.Literal(true), released: Schema.Boolean })
const Config = Schema.Struct({ audience: Schema.String })
const Failure = Schema.Struct({ error: Schema.String })
const ClientID = Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(128))

export type CloudAuth = Schema.Schema.Type<typeof Auth>
export type CloudConfig = Schema.Schema.Type<typeof Config>
export type CloudWorkspace = Schema.Schema.Type<typeof Workspace>
export type CloudSignIn = { verificationUrl: string; userCode: string; interval: number; expiresAt: number }
export type CloudPoll =
  | { state: "pending"; interval: number }
  | { state: "authenticated"; profile: SairAccount.Profile; clientID: string }
export type CloudLease = {
  workspace: CloudWorkspace
  url: string
  expiresAt: number
  authMode: "sair"
  protocol: 1
}
export type CloudRenewal = { state: string; expiresAt: number }
export type CloudRequestOptions = { signal?: AbortSignal; timeoutMs?: number }
export type CloudApiErrorCode =
  | "unauthorized"
  | "forbidden"
  | "conflict"
  | "unavailable"
  | "rate_limited"
  | "invalid_request"
  | "invalid_response"
  | "http"
  | "network"
  | "timeout"
  | "aborted"
  | "not_configured"

export class CloudApiError extends Error {
  readonly name = "CloudApiError"

  constructor(
    readonly code: CloudApiErrorCode,
    readonly status?: number,
    readonly serverMessage?: string,
  ) {
    // Presentation translates the code; upstream detail is retained separately.
    super(code)
  }
}

export function createCloudApi(
  input: {
    origin?: string
    browserOrigin?: string
    fetch?: typeof globalThis.fetch
    timeoutMs?: number
  } = {},
) {
  const origin = input.origin ?? globalThis.location.origin
  const base = URL.parse(origin)
  if (
    !base ||
    base.username ||
    base.password ||
    base.pathname !== "/" ||
    base.search ||
    base.hash ||
    (base.protocol !== "https:" &&
      !(base.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(base.hostname)))
  )
    throw new CloudApiError("invalid_request")
  const fetch = input.fetch ?? globalThis.fetch
  const mode = base.origin === (input.browserOrigin ?? base.origin) ? "same-origin" : "cors"
  const request = async <A>(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    schema: Schema.Codec<A, unknown>,
    options: CloudRequestOptions = {},
    body?: unknown,
    statuses: number[] = [200],
    parse?: (text: string, response: Response) => A,
  ) => {
    const timeoutMs = options.timeoutMs ?? input.timeoutMs ?? 20_000
    if (!Number.isFinite(timeoutMs) || timeoutMs < 1 || timeoutMs > 60_000) throw new CloudApiError("invalid_request")
    const controller = new AbortController()
    const timeout = new CloudApiError("timeout")
    const timer = setTimeout(() => controller.abort(timeout), timeoutMs)
    const signal = options.signal ? AbortSignal.any([options.signal, controller.signal]) : controller.signal
    const execute = async () => {
      signal.throwIfAborted()
      const response = await fetch(new URL(path, base.origin), {
        method,
        credentials: "include",
        mode,
        redirect: "error",
        cache: "no-store",
        signal,
        headers: {
          Accept: parse ? "application/octet-stream" : "application/json",
          ...(body === undefined ? {} : { "Content-Type": "application/json" }),
        },
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      })
      const text = await response.text()
      signal.throwIfAborted()
      if (!response.ok) {
        const failure = Schema.decodeUnknownOption(Schema.fromJsonString(Failure))(text)
        throw new CloudApiError(
          response.status === 503 && Option.isSome(failure) && failure.value.error === "web_not_configured"
            ? "not_configured"
            : responseCode(response.status),
          response.status,
          Option.isSome(failure) ? failure.value.error : undefined,
        )
      }
      if (!statuses.includes(response.status)) throw new CloudApiError("invalid_response", response.status)
      if (parse) return { value: parse(text, response), status: response.status }
      const decoded = Schema.decodeUnknownOption(Schema.fromJsonString(schema))(text)
      if (Option.isNone(decoded)) throw new CloudApiError("invalid_response", response.status)
      return { value: decoded.value, status: response.status }
    }
    return execute()
      .catch((error: unknown) => {
        if (signal.aborted) throw signal.reason === timeout ? timeout : new CloudApiError("aborted")
        if (error instanceof CloudApiError) throw error
        throw new CloudApiError("network")
      })
      .finally(() => clearTimeout(timer))
  }
  return {
    config: async (options?: CloudRequestOptions): Promise<CloudConfig> => {
      const result = await request("/v1/web/config", "GET", Config, options)
      const audience = URL.parse(result.value.audience)
      if (
        !audience ||
        audience.protocol !== "https:" ||
        audience.username ||
        audience.password ||
        audience.pathname !== "/" ||
        audience.search ||
        audience.hash
      )
        throw new CloudApiError("invalid_response", result.status)
      return { audience: audience.origin }
    },
    auth: (options?: CloudRequestOptions): Promise<CloudAuth> =>
      request("/v1/auth", "GET", Auth, options).then((result) => result.value),
    start: async (clientID: string, options?: CloudRequestOptions): Promise<CloudSignIn> => {
      validate(ClientID, clientID)
      const result = await request("/v1/auth/start", "POST", SignIn, options, { client_id: clientID })
      const verification = URL.parse(result.value.verification_url)
      if (!verification || verification.protocol !== "https:" || verification.username || verification.password)
        throw new CloudApiError("invalid_response", result.status)
      return {
        verificationUrl: verification.href,
        userCode: result.value.user_code,
        interval: result.value.interval,
        expiresAt: result.value.expires_at * 1000,
      }
    },
    poll: async (options?: CloudRequestOptions): Promise<CloudPoll> => {
      const result = await request("/v1/auth/poll", "POST", Poll, options, {}, [200, 202])
      if (result.status === 202 && "state" in result.value) return result.value
      if (result.status !== 200 || !("authenticated" in result.value))
        throw new CloudApiError("invalid_response", result.status)
      return { state: "authenticated", profile: result.value.profile, clientID: result.value.client_id }
    },
    connect: async (clientID: string, options?: CloudRequestOptions): Promise<CloudLease> => {
      validate(ClientID, clientID)
      const result = await request("/v1/workspace/leases", "POST", Lease, options, { client_id: clientID }, [202])
      if (result.value.connection.url !== base.origin || !result.value.workspace.id)
        throw new CloudApiError("invalid_response", result.status)
      // The gateway also returns a lease password for native clients. Browser
      // connections use HttpOnly cookies; never expose or persist that password.
      return {
        workspace: result.value.workspace,
        url: base.origin,
        expiresAt: result.value.expires_at * 1000,
        authMode: result.value.auth_mode,
        protocol: result.value.protocol,
      }
    },
    renew: async (active = false, options?: CloudRequestOptions): Promise<CloudRenewal> => {
      const result = await request("/v1/workspace/lease", "PUT", Renewal, options, { active })
      return { state: result.value.state, expiresAt: result.value.expires_at * 1000 }
    },
    release: (options?: CloudRequestOptions) =>
      request("/v1/workspace/lease", "DELETE", Released, options).then((result) => result.value),
    details: (options?: CloudRequestOptions): Promise<RemoteWorkspace.Details> =>
      request("/v1/workspace", "GET", RemoteWorkspace.Details, options).then((result) => result.value),
    sshKey: (options?: CloudRequestOptions): Promise<RemoteWorkspace.InitialSSHKey> =>
      request(
        "/v1/workspace/ssh-key/download",
        "GET",
        RemoteWorkspace.InitialSSHKey,
        options,
        undefined,
        [200],
        (privateKey, response) => {
          const filename = /filename="(ws-[a-f0-9]{24}\.key)"/.exec(
            response.headers.get("content-disposition") ?? "",
          )?.[1]
          if (
            response.headers.get("content-type") !== "application/octet-stream" ||
            !filename ||
            privateKey.length > 16384 ||
            !privateKey.startsWith("-----BEGIN OPENSSH PRIVATE KEY-----\n") ||
            !privateKey.trimEnd().endsWith("-----END OPENSSH PRIVATE KEY-----")
          )
            throw new CloudApiError("invalid_response", response.status)
          return { filename, privateKey }
        },
      ).then((result) => result.value),
    resize: (input: RemoteWorkspace.Resize, options?: CloudRequestOptions): Promise<CloudWorkspace> =>
      request(
        "/v1/workspace/resources",
        "POST",
        Workspace,
        options,
        validate(RemoteWorkspace.Resize, input),
        [200, 202],
      ).then((result) => result.value),
    // This edge endpoint clears only this browser's cookies and lease. It does
    // not call the gateway's account-wide logout or revoke its saved session.
    logoutBrowser: (options?: CloudRequestOptions) =>
      request("/v1/auth/browser-logout", "POST", BrowserLogout, options, {}).then((result) => result.value),
  }
}

export type CloudApi = ReturnType<typeof createCloudApi>

function validate<A>(schema: Schema.Codec<A, unknown>, input: unknown) {
  const result = Schema.decodeUnknownOption(schema)(input)
  if (Option.isNone(result)) throw new CloudApiError("invalid_request")
  return result.value
}

function responseCode(status: number): CloudApiErrorCode {
  if (status === 401) return "unauthorized"
  if (status === 403) return "forbidden"
  if (status === 409) return "conflict"
  if (status === 429) return "rate_limited"
  if (status === 400 || status === 422) return "invalid_request"
  if (status === 502 || status === 503 || status === 504) return "unavailable"
  return "http"
}
